import {
  onMounted,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type WatchHandle,
} from 'vue';

import { ProjectSettings } from '@/shared/lib';

const useAppInitialLoading = (isFetched: MaybeRefOrGetter<boolean>) => {
  const isInitialLoadingVisible = ref<boolean>(true);

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let stopWatching: WatchHandle | null = null;

  const syncIsFetched = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    if (stopWatching !== null) {
      stopWatching();
    }

    isInitialLoadingVisible.value = false;
  };

  const startWatchingIsFetched = () => {
    if (toValue(isFetched)) {
      syncIsFetched();
      return;
    }

    stopWatching = watch(
      () => toValue(isFetched),
      (fetched) => {
        if (fetched) {
          isInitialLoadingVisible.value = true;
        }
      },
      { immediate: true },
    );
  };

  onMounted(() => {
    if (toValue(isFetched)) {
      syncIsFetched();
    } else {
      if (ProjectSettings.loading.screenDelay !== false) {
        timeoutId = setTimeout(startWatchingIsFetched, ProjectSettings.loading.screenDelay);
      } else {
        syncIsFetched();
      }
    }
  });

  onScopeDispose(() => {
    if (stopWatching !== null) {
      stopWatching();
    }

    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  });

  return { isInitialLoadingVisible };
};

export { useAppInitialLoading };
